import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { PlayArrow, OndemandVideo } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { ASSETS_BASE_URL, SERVER_BASE_URL } from '../../../config';
import { useTranslation } from 'react-i18next';
import ContactCCRForm from '../../../components/ContactCCRForm/ContactCCRForm';
import { getArchiveDirectory } from '../../../services/documentService';
import DocumentViewModal from '../../../components/DocumentViewModal';
import { trackEvent } from '../../../utilities/analyticsService';

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */

const ROOT_PATH = 'AMF Premium';
const LANGUAGE_FOLDERS = {
    en: 'English',
    fr: 'French',
    es: 'Spanish Videos',
};

/**
 * What the language picker offers, in the order shown.
 *
 * Each label is written in its own language so a viewer who cannot read the current one can still
 * find theirs. Keyed to LANGUAGE_FOLDERS, so adding a language means adding the folder there and
 * an entry here — nothing else on the page needs to know.
 */
const LANGUAGE_OPTIONS = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' },
];

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v', 'avi', 'mkv', 'mpeg', 'mpg']);

/**
 * Resolve the base URL for static assets.
 * Priority: ASSETS_BASE_URL env → SERVER_BASE_URL/assets → /assets
 */
const DEFAULT_ASSET_BASE = (() => {
    const serverBase = SERVER_BASE_URL?.replace(/\/$/, '') || '';
    if (ASSETS_BASE_URL) return ASSETS_BASE_URL;
    if (serverBase) return `${serverBase}/assets`;
    return '/assets';
})();

/**
 * Each video has a `thumbnailFallback` — a static PNG served from your
 * own origin (no CORS issues).  This is the FIRST thing we try to show;
 * canvas capture from the Azure video is only attempted when this image
 * also fails to load (404 / path wrong / etc.).
 *
 * ⚠️  Fix the typo that was in the previous version:
 *     "amf_premium_learnuing_module"  →  "amf_premium_learning_module"
 */
const DEFAULT_THUMBNAILS = [
    `${DEFAULT_ASSET_BASE}/ImageBoxes/amf_premiums_knowledge_articles.png`,
    `${DEFAULT_ASSET_BASE}/ImageBoxes/amf_premium_learning_module.png`,
];

/* ─────────────────────────────────────────────
   Utilities
───────────────────────────────────────────── */

const getFileExtension = (name = '') =>
    String(name || '').split('?')[0].split('.').pop().toLowerCase();

/** Try every common thumbnail field Azure might return. */
const getFileThumbnail = (file = {}, fallback = null) => {
    if (!file) return fallback;
    return (
        file.thumbnail ||
        file.thumbnail_url ||
        file.preview_thumbnail ||
        file.previewThumbnail ||
        file.image ||
        file.cover_image ||
        file.previewImage ||
        fallback
    );
};

const humanizeFileName = (name = '') =>
    String(name || '')
        .replace(/\.[^.]+$/, '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Canvas-based thumbnail capture — LAST resort only.
 *
 * Azure Blob Storage often blocks canvas reads with CORS errors.
 * We attempt it anyway; any failure resolves null silently so the
 * caller can fall through to the next option.
 */
const generateVideoThumbnail = (videoUrl, seekTime = 2) =>
    new Promise((resolve) => {
        if (!videoUrl) return resolve(null);

        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        video.src = videoUrl;

        let settled = false;
        let timer = null;

        const settle = (val) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            video.src = '';
            video.load();
            resolve(val);
        };

        const capture = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth || 1280;
                canvas.height = video.videoHeight || 720;
                canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
                settle(canvas.toDataURL('image/jpeg', 0.85));
            } catch {
                settle(null);
            }
        };

        video.addEventListener('loadeddata', () => {
            video.currentTime = Math.min(seekTime, video.duration || seekTime);
        });
        video.addEventListener('seeked', capture, { once: true });
        video.addEventListener('error', () => settle(null));

        timer = setTimeout(() => settle(null), 8000);
    });

/* ─────────────────────────────────────────────
   VideoHeroCard
   Uses an <img> onError chain so the card NEVER
   stays black if any fallback is available.
───────────────────────────────────────────── */

const VideoHeroCard = ({ video, height, onClick, badgeLabel }) => {
    const [imgSrc, setImgSrc] = useState(video?.thumbnail || null);
    const [imgFailed, setImgFailed] = useState(false);

    // Sync if the parent updates the video object (e.g. after canvas generation)
    useEffect(() => {
        setImgSrc(video?.thumbnail || null);
        setImgFailed(false);
    }, [video?.thumbnail]);

    if (!video) return null;

    /**
     * Called when the <img> src fails to load.
     * Tries the static fallback PNG next; if that also fails, hides the
     * image entirely (black background is shown — better than a broken icon).
     */
    const handleImgError = () => {
        if (!imgFailed && video.thumbnailFallback && imgSrc !== video.thumbnailFallback) {
            setImgSrc(video.thumbnailFallback);
        } else {
            setImgFailed(true);
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                backgroundColor: '#111',
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                height,
                cursor: 'pointer',
                '&:hover .play-circle': {
                    transform: 'scale(1.1)',
                    backgroundColor: 'rgba(255,255,255,1)',
                },
                '&:hover .card-img': {
                    transform: 'scale(1.03)',
                },
            }}
            onClick={onClick}
        >
            {/* Thumbnail — hidden if both src and fallback fail */}
            {imgSrc && !imgFailed ? (
                <Box
                    component="img"
                    className="card-img"
                    src={imgSrc}
                    alt={video.title}
                    onError={handleImgError}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',          // show full image
                        objectPosition: 'center',      //  center it
                        backgroundColor: '#111',       //  fill empty space nicely
                        padding: '8px',                //  optional: prevents edge sticking
                        transition: 'transform 0.4s ease',
                        display: 'block',
                    }}
                />
            ) : (
                /* Placeholder shown when no image is available */
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#111',
                    }}
                >
                    <OndemandVideo sx={{ fontSize: 64, color: 'rgba(255,255,255,0.15)' }} />
                </Box>
            )}

            {/* Gradient overlay — always rendered so text is readable */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                        'linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.75) 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 2,
                }}
            >
                {/* Top-right: content type badge */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: '#555',
                        }}
                    >
                        {badgeLabel}
                    </Box>
                </Box>

                {/* Bottom: play button + title */}
                <Box>
                    <Box
                        className="play-circle"
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.92)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1.5,
                            transition: 'transform 0.2s ease, background-color 0.2s ease',
                        }}
                    >
                        <PlayArrow sx={{ fontSize: 30, color: '#E21A23' }} />
                    </Box>

                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                        {video.title}
                    </Typography>

                    {video.description ? (
                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.86)',
                                fontSize: '0.86rem',
                                lineHeight: 1.5,
                                mb: 0.75,
                            }}
                        >
                            {video.description}
                        </Typography>
                    ) : null}

                    {/* <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.83rem' }}>
                        {video.name}
                    </Typography> */}
                </Box>
            </Box>
        </Box>
    );
};

/* ─────────────────────────────────────────────
   TrainingDashboard
───────────────────────────────────────────── */

const TrainingDashboard = () => {
    const { t } = useTranslation();
    const [contactOpen, setContactOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [trainingVideos, setTrainingVideos] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    // Always starts at English rather than following the browser or the app's current language:
    // this picks which VIDEOS the user watches, not just which words they read, and the English
    // recordings are the ones that always exist. A viewer who wants another language chooses it.
    const [language, setLanguage] = useState('en');

    /**
     * Translate for THIS page in the chosen language, without touching the rest of the app.
     *
     * i18n.changeLanguage would have been one line, but it is global — picking French to watch a
     * video would leave the whole portal in French after navigating away. Passing `lng` per call
     * keeps the choice scoped to this page. Every locale is bundled statically in i18n.js, so
     * there is nothing to load first.
     *
     * @param {string} key Translation key.
     * @param {object} [opts] Any other i18next options, e.g. defaultValue.
     * @returns {string} The translated string in the currently selected language.
     */
    const tl = useCallback((key, opts = {}) => t(key, { lng: language, ...opts }), [t, language]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // ?open_premium_form=1 deep link (CHIP's open_training_premium) — auto-opens the AMF
    // Premium Request Form on arrival, same as clicking "Interested in Premium?" directly,
    // so a chat request to sign up/learn more about Premium doesn't need an extra click
    // once landed on this page. Mirrors Cases.js's ?open_case= deep-link pattern: read once,
    // then strip the param so it can't re-fire on a later re-render or resurrect on
    // back-navigation. No data-loading race to guard here (contactOpen is a plain UI flag,
    // not dependent on an async fetch), so a one-shot ref is enough.
    const openPremiumFormHandledRef = useRef(false);
    useEffect(() => {
        if (openPremiumFormHandledRef.current) return;
        const params = new URLSearchParams(location.search);
        if (!params.get('open_premium_form')) return;
        openPremiumFormHandledRef.current = true;
        setContactOpen(true);
        params.delete('open_premium_form');
        const newSearch = params.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Live-reported 2026-07-31: closing the form, then asking CHIP again, didn't reopen it.
    // Root cause: a repeat ask navigates to the SAME "/training?open_premium_form=1" URL —
    // React Router doesn't always reconcile a location-search effect on a textually IDENTICAL
    // destination (the "same-route no-op" class Cases.js's own ?open_case= handler already
    // works around), so the effect above never re-fires. Mirrors Cases.js's dedicated
    // "chip:navigate" listener exactly: ChatBot.js dispatches this event synchronously with
    // the real destination path on EVERY chat-driven navigate, independent of location-diffing
    // timing, so it reopens the form every time regardless of whether the URL actually changed.
    useEffect(() => {
        const onChipNavigateOpenPremiumForm = (e) => {
            const dest = String(e?.detail?.path || '');
            if (!dest.startsWith('/training')) return;
            const qIndex = dest.indexOf('?');
            const searchString = qIndex >= 0 ? dest.slice(qIndex) : '';
            if (!new URLSearchParams(searchString).get('open_premium_form')) return;
            setContactOpen(true);
        };
        window.addEventListener('chip:navigate', onChipNavigateOpenPremiumForm);
        return () => window.removeEventListener('chip:navigate', onChipNavigateOpenPremiumForm);
    }, []);

    const loadVideos = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            // Driven by the picker, not navigator.language. The browser's locale used to choose the
            // folder while the surrounding copy came from i18n, so the two could disagree — French
            // headings over English videos — and nothing on the page let a viewer correct it.
            const resolveLanguageFolder = (directories = []) => {
                const folderName = LANGUAGE_FOLDERS[language] || LANGUAGE_FOLDERS.en;

                const match = directories.find(
                    (dir) => String(dir?.name || '').trim().toLowerCase() === folderName.toLowerCase()
                );

                return match?.path || null;
            };

            const rootResponse = await getArchiveDirectory(ROOT_PATH);
            const rootPayload = rootResponse?.data || rootResponse || {};
            const rootDirs = Array.isArray(rootPayload?.directories) ? rootPayload.directories : [];

            const languagePath = resolveLanguageFolder(rootDirs) || ROOT_PATH;
            const response = await getArchiveDirectory(languagePath);
            const payload = response?.data || response || {};
            const files = Array.isArray(payload?.files) ? payload.files : [];

            // Captions ride in the same directory listing as the videos, so pairing them costs
            // no extra request: a video is captioned when a .vtt with the same basename sits
            // beside it. Keyed on the lower-cased basename because Azure preserves the case the
            // file was uploaded with, and the two halves of a pair are rarely uploaded together.
            const captionsByBaseName = new Map();
            files.forEach((file) => {
                const fileName = file?.name || file?.path?.split('/').pop() || '';
                if (getFileExtension(fileName) !== 'vtt') return;
                captionsByBaseName.set(fileName.replace(/\.[^.]+$/, '').toLowerCase(), file.path);
            });

            const azureVideos = files
                .filter((file) => VIDEO_EXTENSIONS.has(getFileExtension(file?.name || file?.path)))
                .map((file, index) => {
                    const resolvedName = file?.name || file?.path?.split('/').pop() || `Video ${index + 1}`;
                    const normalizedTitle = humanizeFileName(resolvedName).replace(/\s+\(Music\)$/i, '');
                    const metaThumbnail = getFileThumbnail(file, null);
                    const fallbackThumb = DEFAULT_THUMBNAILS[index % DEFAULT_THUMBNAILS.length];

                    return {
                        id: file.path || file.name || `training-video-${index + 1}`,
                        title: normalizedTitle,
                        language: String(languagePath || '').split('/').pop() || 'EN',
                        thumbnail: metaThumbnail || fallbackThumb,
                        thumbnailFallback: fallbackThumb,
                        name: resolvedName,
                        path: file.path,
                        captionPath:
                            captionsByBaseName.get(resolvedName.replace(/\.[^.]+$/, '').toLowerCase()) || null,
                        captionLang: language,
                    };
                });
            const labeledVideos = azureVideos.map((video) => {
                const name = (video.name || '').toLowerCase();

                // Knowledge Articles
                if (name.includes('knowledge')) {
                    return {
                        ...video,
                        title: tl('TRAINING_DASHBOARD.VIDEO_CARDS.KNOWLEDGE_ARTICLES.TITLE'),
                        description: tl('TRAINING_DASHBOARD.VIDEO_CARDS.KNOWLEDGE_ARTICLES.DESCRIPTION'),
                        displayName: 'AMF Premium Knowledge Articles',
                        type: 'knowledge',
                    };
                }

                // Learning Modules
                if (name.includes('learning')) {
                    return {
                        ...video,
                        title: tl('TRAINING_DASHBOARD.VIDEO_CARDS.LEARNING_MODULES.TITLE'),
                        description: tl('TRAINING_DASHBOARD.VIDEO_CARDS.LEARNING_MODULES.DESCRIPTION'),
                        displayName: 'AMF Premium Learning Modules',
                        type: 'learning',
                    };
                }

                return {
                    ...video,
                    type: 'other',
                };
            });
            // (Azure metadata AND static fallback both absent)
            const withThumbnails = await Promise.all(
                labeledVideos.map(async (video) => {
                    if (video.thumbnail) return video;

                    const videoUrl = `${ASSETS_BASE_URL}/${video.path}`;
                    const generated = await generateVideoThumbnail(videoUrl);

                    return {
                        ...video,
                        thumbnail: generated || video.thumbnailFallback || null,
                    };
                })
            );

            setTrainingVideos(withThumbnails);
            setSelectedVideo((prev) => {
                if (prev) {
                    return (
                        withThumbnails.find((v) => v.path === prev.path) ||
                        withThumbnails[0] ||
                        null
                    );
                }
                return withThumbnails[0] || null;
            });
        } catch (err) {
            console.error('Failed to load AMF Premium videos:', err);
            setError(
                tl('TRAINING_DASHBOARD.ERROR_LOADING_VIDEOS', {
                    defaultValue: 'Unable to load training videos right now.',
                })
            );
            setTrainingVideos([]);
            setSelectedVideo(null);
        } finally {
            setLoading(false);
        }
    }, [tl, language]);

    useEffect(() => {
        loadVideos();
    }, [loadVideos]);

    const allVideos = useMemo(() => {
        const learning = trainingVideos.find(v => v.type === 'learning');
        const knowledge = trainingVideos.find(v => v.type === 'knowledge');

        return [learning, knowledge].filter(Boolean);
    }, [trainingVideos]);
    const getBadgeLabel = (video) => {
        if (video?.type === 'knowledge') {
            return tl('TRAINING_DASHBOARD.KNOWLEDGE_ARTICLES');
        }
        return tl('TRAINING_DASHBOARD.LEARNING_PATH');
    };

    const handlePremiumClick = () => {
        trackEvent('premium_interest_click', {
            element_label: 'Sign Up Premium',
        });
        setContactOpen(true);
    };

    const handleVideoClick = (video) => {
        if (!video?.path) return;
        trackEvent('premium_video_view', {
            video_title: video.title || '',
            video_type: video.type || '',
            element_label: video.title || '',
        });
        setSelectedVideo(video);
        setModalOpen(true);
    };

    // ?play_video=learning|knowledge deep link (CHIP's open_training_video) — auto-plays
    // the matching Training-page preview video through the SAME handleVideoClick a manual
    // click uses (so the premium_video_view tracking fires identically). Unlike
    // ?open_premium_form above, the videos load asynchronously, so the param is captured
    // into state here and resolved by the effect below once allVideos is populated.
    const [videoToPlayParam, setVideoToPlayParam] = useState(null);
    const playVideoHandledRef = useRef(false);
    useEffect(() => {
        if (playVideoHandledRef.current) return;
        const params = new URLSearchParams(location.search);
        const which = params.get('play_video');
        if (!which) return;
        playVideoHandledRef.current = true;
        setVideoToPlayParam(which);
        params.delete('play_video');
        const newSearch = params.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Same "chip:navigate" listener pattern as the premium-form deep link above — a repeat
    // ask navigates to a textually identical URL, which React Router may not reconcile, so
    // the location-search effect alone would miss every request after the first.
    useEffect(() => {
        const onChipNavigatePlayVideo = (e) => {
            const dest = String(e?.detail?.path || '');
            if (!dest.startsWith('/training')) return;
            const qIndex = dest.indexOf('?');
            const which = new URLSearchParams(qIndex >= 0 ? dest.slice(qIndex) : '').get('play_video');
            if (which) setVideoToPlayParam(which);
        };
        window.addEventListener('chip:navigate', onChipNavigatePlayVideo);
        return () => window.removeEventListener('chip:navigate', onChipNavigatePlayVideo);
    }, []);

    // Resolve once the videos are loaded; falls back to the first video if the requested
    // type is missing (e.g. only one video is published for the selected language).
    useEffect(() => {
        if (!videoToPlayParam || !allVideos.length) return;
        const target = allVideos.find((v) => v.type === videoToPlayParam) || allVideos[0];
        setVideoToPlayParam(null);
        if (target) handleVideoClick(target);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoToPlayParam, allVideos]);

    return (
        <>
            <Box sx={{ p: 0.6 }}>
                {/* Page title */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <PageTitle title={tl('TRAINING_DASHBOARD.HEADER')} />
                </Box>

                {/* Intro + sign-up row */}
                <Box sx={{ p: 3, mb: 3 }}>
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '1100px',
                            mx: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'center', md: 'space-between' },
                            gap: 6,
                            flexWrap: { xs: 'wrap', md: 'nowrap' },
                        }}
                    >
                        <Box sx={{ maxWidth: '750px', textAlign: { xs: 'center', md: 'left' } }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
                                {tl('TRAINING_DASHBOARD.TRAINING_OVERVIEW.TITLE_PART_1')}{' '}
                                <span style={{ color: '#E21A23' }}>
                                    {tl('TRAINING_DASHBOARD.TRAINING_OVERVIEW.TITLE_TRAINING')}
                                </span>{' '}
                                {tl('TRAINING_DASHBOARD.TRAINING_OVERVIEW.TITLE_PART_2')}
                            </Typography>

                            <Typography
                                sx={{
                                    fontStyle: 'italic',
                                    lineHeight: 1.6,
                                    fontSize: '1rem',
                                    textAlign: 'left',
                                }}
                            >
                                {tl('TRAINING_DASHBOARD.TRAINING_OVERVIEW.DESC_PART_1')}{' '}
                                <b style={{ color: '#E21A23' }}>
                                    {tl('TRAINING_DASHBOARD.TRAINING_OVERVIEW.DESC_PREMIUM')}
                                </b>
                            </Typography>
                        </Box>

                        {/* Column so the picker sits UNDER the button rather than becoming a third
                            item in the row above. Both are 250px wide, so their edges line up. */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: { xs: 'center', md: 'flex-start' },
                                gap: 2,
                            }}
                        >
                            {/* 8px, not the square corners this had before: it is what
                                ThemeContext gives MuiOutlinedInput/MuiAlert and what every
                                toolbar button renders at, so the picker below lines up with it
                                instead of sitting under a shape found nowhere else in the app. */}
                            <Button
                                variant="contained"
                                onClick={handlePremiumClick}
                                sx={{
                                    backgroundColor: '#E21A23',
                                    color: '#fff',
                                    textTransform: 'uppercase',
                                    fontWeight: 700,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '0.875rem',
                                    letterSpacing: '0.5px',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(226,26,35,0.3)',
                                    minWidth: '250px',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        backgroundColor: '#c71820',
                                        boxShadow: '0 4px 12px rgba(226,26,35,0.4)',
                                    },
                                }}
                            >
                                {tl('TRAINING_DASHBOARD.SIGN_UP_PREMIUM')}
                            </Button>

                            {/* Belongs to this page, not the app: it switches both the copy and
                                which language's recordings play, and the footer's global selector
                                does neither. Each option is written in its own language so it is
                                recognisable to someone who cannot read the current one.

                                No radius/colour overrides on purpose — ThemeContext already gives
                                MuiOutlinedInput the app's 8px corners and white fill, and
                                `color="primary"` is the tenant accent, so the focus ring follows a
                                rebrand instead of pinning the red in place. Only the 250px is set
                                here, to line its edges up with the button above. */}
                            <TextField
                                select
                                size="small"
                                color="primary"
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                                label={tl('TRAINING_DASHBOARD.LANGUAGE_LABEL', { defaultValue: 'Language' })}
                                sx={{ minWidth: '250px' }}
                            >
                                {LANGUAGE_OPTIONS.map((option) => (
                                    <MenuItem key={option.code} value={option.code}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Video cards — equal 50/50 */}
                <Box sx={{ px: 3, mb: 10 }}>
                    <Box sx={{ width: '100%', maxWidth: '1100px', mx: 'auto' }}>
                        <Grid container spacing={4} alignItems="stretch">
                            {loading ? (
                                Array.from({ length: 2 }).map((_, i) => (
                                    <Grid item xs={12} md={6} key={`skeleton-${i}`}>
                                        <Card
                                            elevation={2}
                                            sx={{
                                                width: '100%',
                                                height: { xs: '240px', md: '320px' },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#111',
                                                borderRadius: 1,
                                            }}
                                        >
                                            <CircularProgress sx={{ color: '#fff' }} />
                                        </Card>
                                    </Grid>
                                ))
                            ) : allVideos.length > 0 ? (
                                allVideos.map((video) => (
                                    <Grid item xs={12} md={6} key={video.id}>
                                        <VideoHeroCard
                                            video={video}
                                            height={{ xs: '240px', md: '320px' }}
                                            onClick={() => handleVideoClick(video)}
                                            badgeLabel={getBadgeLabel(video)}
                                        />
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            height: { xs: '240px', md: '320px' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#111',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography sx={{ color: '#fff', textAlign: 'center' }}>
                                            {tl('TRAINING_DASHBOARD.NO_VIDEOS_FOUND', {
                                                defaultValue: 'No training videos found.',
                                            })}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Box>
            </Box>

            <DocumentViewModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                filePath={selectedVideo?.path || null}
                fileName={selectedVideo?.name || null}
                displayName={selectedVideo?.displayName || null}
                captionPath={selectedVideo?.captionPath || null}
                captionLang={selectedVideo?.captionLang || 'en'}
                showBreadcrumbs={false}
                showReloadButton={false}
                showDownloadButton={false}
            />

            <ContactCCRForm
                open={contactOpen}
                setOpen={setContactOpen}
                mode="premium"
                showEmergencyNotice={false}
                prefill={{
                    subject: tl('PREMIUM_ACCESS.EMAIL.SUBJECT'),
                    message: tl('PREMIUM_ACCESS.EMAIL.MESSAGE'),
                }}
            />
        </>
    );
};

export default TrainingDashboard;
